import { type MaybeRefOrGetter, computed, toValue } from 'vue'

import { MarketCountryCode } from '@backmarket/http-api/src/api-models/MarketCountryCode'
import { useExperiments } from '@backmarket/nuxt-module-experiments/useExperiments'
import { useMarketplace } from '@backmarket/nuxt-module-marketplace/useMarketplace'

const VARIANT_BY_SLUG = {
  smartphones: 'smartphones',
  iphone: 'smartphones',
  samsung: 'smartphones',
  'google-pixel': 'smartphones',
  'android-smartphones': 'smartphones',
  'computers-laptops': 'laptops',
  'apple-macbook': 'laptops',
  'windows-laptops': 'laptops',
  'chromebook-laptop': 'laptops',
  '2-in-1-hybrid-pcs': 'laptops',
  'gaming-laptops': 'laptops',
} as const

type BatterySlug = keyof typeof VARIANT_BY_SLUG
export type BatteryVariant = (typeof VARIANT_BY_SLUG)[BatterySlug]

const IMAGE_BY_VARIANT: Record<BatteryVariant, string> = {
  smartphones: '/img/plp/battery-block-smartphones.jpg',
  laptops: '/img/plp/battery-block-laptops.jpg',
}

export type BatteryBlockData = {
  variant: BatteryVariant
  src: string
}

/**
 * Resolves the battery education block for a PLP. Returns `undefined` when
 * the slug, market, or experiment make the block ineligible.
 */
export function useBatteryBlock(slug: MaybeRefOrGetter<string | undefined>) {
  const experiments = useExperiments()
  const { market } = useMarketplace()

  const batteryBlock = computed<BatteryBlockData | undefined>(() => {
    if (market.countryCode !== MarketCountryCode.US) return undefined
    if (experiments['experiment.isBatteryEducationEnabled'] !== 'with')
      return undefined

    const variant = VARIANT_BY_SLUG[toValue(slug) as BatterySlug]
    if (!variant) return undefined

    return { variant, src: IMAGE_BY_VARIANT[variant] }
  })

  return { batteryBlock }
}
